import { useEffect, useId, useState, type KeyboardEvent } from 'react'
import { useBackend } from '../../lib/session'
import type { Thought } from '../../lib/types'
import { thoughtKind, thoughtStamp } from '../../lib/text'
import { btn, errorText } from '../ui'
import type { ThoughtChoice } from './insertChoices'

const TITLE: Record<ThoughtChoice, string> = {
  thought: 'THOUGHT · SOURCED, IN THE COLUMN',
  margin: 'MARGIN NOTE · BESIDE THE PARAGRAPH',
}

export const PICKER_LABEL: Record<ThoughtChoice, string> = {
  thought: 'Thought',
  margin: 'Margin note',
}

interface Props {
  kind: ThoughtChoice
  /** A margin note on selected words: the words it's about. */
  words?: string
  onPick: (thought: Thought) => void
  onOwn: (text: string) => void
  onCancel: () => void
}

/**
 * The compact picker behind the "From your thoughts" choices: search your
 * saved thoughts, or write your own words instead. For a margin note, what's
 * typed in the search can be the note itself (Niki, 10/2): a top row "Write
 * ‘…’ as a note", which ↵ takes unless a thought is arrowed to.
 */
export function ThoughtPicker({ kind, words, onPick, onOwn, onCancel }: Props) {
  const backend = useBackend()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Thought[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const [writing, setWriting] = useState(false)
  const [own, setOwn] = useState('')
  // A margin note written straight from the search: the row above the thoughts.
  const typed = kind === 'margin' ? query.trim() : ''
  const lead = typed ? 1 : 0

  useEffect(() => {
    let alive = true
    const t = setTimeout(
      () => {
        backend
          .listThoughts({ search: query.trim() || undefined, limit: 6 })
          .then((r) => {
            if (!alive) return
            setItems(r)
            setActive(0)
            setError(null)
          })
          .catch((e: unknown) => alive && setError(errorText(e)))
      },
      query ? 160 : 0,
    )
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [backend, query])

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = (items?.length ?? 0) + lead
    if (e.key === 'ArrowDown' && n) {
      e.preventDefault()
      setActive((a) => (a + 1) % n)
    } else if (e.key === 'ArrowUp' && n) {
      e.preventDefault()
      setActive((a) => (a - 1 + n) % n)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (lead && active === 0) onOwn(typed)
      else if (items?.[active - lead]) onPick(items[active - lead])
    }
  }

  const title = kind === 'margin' && words ? `MARGIN NOTE ON “${words}”` : TITLE[kind]
  return (
    <div
      data-keep-selection
      role="dialog"
      aria-label={`${PICKER_LABEL[kind]} from your thoughts`}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
      onClick={(e) => e.stopPropagation()}
      className="relative z-20 mb-[22px] flex w-full max-w-[520px] flex-col gap-1.5 rounded-2xl border border-border/50 bg-card p-2 font-sans text-foreground shadow-[var(--shadow-pop)]"
    >
      <div className="flex items-center justify-between gap-3 px-2.5 pt-1">
        <span className="min-w-0 truncate text-[10.5px] font-semibold tracking-[0.06em] text-muted-foreground">{title}</span>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-full text-[16px] leading-none text-muted-foreground hover:bg-foreground/5"
        >
          ×
        </button>
      </div>

      {writing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (own.trim()) onOwn(own.trim())
          }}
          className="flex flex-col gap-2 px-1 pb-1"
        >
          <textarea
            autoFocus
            value={own}
            onChange={(e) => setOwn(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                if (own.trim()) onOwn(own.trim())
              }
            }}
            rows={3}
            aria-label="Your words"
            placeholder="In your own words…"
            className="w-full resize-y rounded-lg border border-border/80 bg-canvas/60 px-3 py-2.5 font-serif text-[15px]/[1.45] italic text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent"
          />
          <div className="flex justify-end gap-1.5">
            <button type="button" onClick={() => setWriting(false)} className={btn.ghost}>
              Back to thoughts
            </button>
            <button type="submit" disabled={!own.trim()} className={btn.primary}>
              Add
            </button>
          </div>
        </form>
      ) : (
        <>
          <input
            autoFocus
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              // New words: ↵ is theirs again (or the first match's) until something is arrowed to.
              setActive(0)
            }}
            onKeyDown={onSearchKey}
            placeholder={kind === 'margin' ? 'Search your thoughts, or write the note' : 'Search your thoughts'}
            aria-label="Search your thoughts"
            aria-controls={listId}
            className="mx-1 rounded-lg border border-border/60 bg-canvas/60 px-3 py-2 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent"
          />
          <div id={listId} role="listbox" aria-label="Your thoughts" className="flex max-h-[300px] flex-col gap-0.5 overflow-y-auto">
            {typed && (
              <div
                role="option"
                aria-selected={active === 0}
                aria-label={`Write “${typed}” as a note`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(0)}
                onClick={() => onOwn(typed)}
                className={`flex cursor-pointer items-baseline gap-2 rounded-lg px-2.5 py-2 ${active === 0 ? 'bg-accent/15' : 'hover:bg-foreground/4'}`}
              >
                <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
                  Write <span className="font-serif text-[14.5px] italic">‘{typed}’</span> as a note
                </span>
                <kbd aria-hidden className="shrink-0 font-sans text-[11px] font-semibold text-muted-foreground">
                  ↵
                </kbd>
              </div>
            )}
            {error ? (
              <p className="m-0 px-2.5 py-3 text-[13px] text-muted-foreground">{error}</p>
            ) : items === null ? (
              <p className="m-0 px-2.5 py-3 text-[13px] text-muted-foreground">Loading thoughts…</p>
            ) : items.length === 0 ? (
              <p className="m-0 px-2.5 py-3 text-[13px] text-muted-foreground">{query ? (typed ? 'No thoughts match.' : 'Nothing matches.') : 'No thoughts yet.'}</p>
            ) : (
              items.map((t, i) => (
                <div
                  key={t.id}
                  role="option"
                  aria-selected={i + lead === active}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i + lead)}
                  onClick={() => onPick(t)}
                  className={`flex cursor-pointer flex-col gap-0.5 rounded-lg px-2.5 py-2 ${i + lead === active ? 'bg-accent/15' : 'hover:bg-foreground/4'}`}
                >
                  <span className="line-clamp-2 font-display text-[14px]/[1.45] text-foreground/85">
                    {t.body || t.title || t.source_title || t.source_url}
                  </span>
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {thoughtStamp(t.created_at)} · {thoughtKind(t)}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-border/40 px-2.5 pb-0.5 pt-2">
            <span className="text-[11px] text-muted-foreground">↑↓ choose · ↵ insert · esc cancel</span>
            <button type="button" onClick={() => setWriting(true)} className="text-[13px] font-semibold text-primary hover:underline">
              Write my own
            </button>
          </div>
        </>
      )}
    </div>
  )
}
